import React, { useState } from 'react';
import { Key, Copy, RotateCcw, Trash2, Eye, EyeOff, TrendingUp, AlertCircle, Check } from 'lucide-react';
import Button from '../../../components/ui/Button';

export function ApiKeyCard({ apiKey, onRevoke, onRegenerate, onViewUsage }) {
  const [showSecret, setShowSecret] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  const copyToClipboard = async (text, setCopied) => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'revoked':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const usagePercentage = (apiKey?.currentUsage / apiKey?.usageLimit) * 100;
  const isNearLimit = usagePercentage >= 80;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-blue-100 p-3 rounded-lg">
            <Key className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{apiKey?.keyName}</h3>
            <p className="text-sm text-gray-500">
              Created {new Date(apiKey.createdAt)?.toLocaleDateString()}
            </p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(apiKey?.status)}`}>
          {apiKey?.status}
        </span>
      </div>
      {/* API Key */}
      <div className="mb-4">
        <label className="text-sm font-medium text-gray-700 mb-2 block">API Key</label>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-gray-50 px-3 py-2 rounded border text-sm font-mono">
            {apiKey?.apiKey}
          </code>
          <button
            onClick={() => copyToClipboard(apiKey?.apiKey, setCopiedKey)}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            title="Copy API Key"
          >
            {copiedKey ? (
              <Check className="w-5 h-5 text-green-600" />
            ) : (
              <Copy className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>
      </div>
      {/* API Secret */}
      <div className="mb-4">
        <label className="text-sm font-medium text-gray-700 mb-2 block">API Secret</label>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-gray-50 px-3 py-2 rounded border text-sm font-mono">
            {showSecret ? apiKey?.apiSecret : '•'?.repeat(48)}
          </code>
          <button
            onClick={() => setShowSecret(!showSecret)}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            title={showSecret ? 'Hide Secret' : 'Show Secret'}
          >
            {showSecret ? (
              <EyeOff className="w-5 h-5 text-gray-600" />
            ) : (
              <Eye className="w-5 h-5 text-gray-600" />
            )}
          </button>
          <button
            onClick={() => copyToClipboard(apiKey?.apiSecret, setCopiedSecret)}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            title="Copy Secret"
          >
            {copiedSecret ? (
              <Check className="w-5 h-5 text-green-600" />
            ) : (
              <Copy className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>
      </div>
      {/* Usage Stats */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Usage</span>
          <span className="text-sm text-gray-600">
            {apiKey?.currentUsage?.toLocaleString()} / {apiKey?.usageLimit?.toLocaleString()}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              isNearLimit ? 'bg-red-500' : 'bg-blue-500'
            }`}
            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
          />
        </div>
        {isNearLimit && (
          <div className="flex items-center gap-2 mt-2 text-sm text-amber-600">
            <AlertCircle className="w-4 h-4" />
            <span>Approaching usage limit</span>
          </div>
        )}
      </div>
      {/* Metadata */}
      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div>
          <span className="text-gray-500">Permissions:</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {apiKey?.permissions?.map(perm => (
              <span key={perm} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                {perm}
              </span>
            ))}
          </div>
        </div>
        <div>
          <span className="text-gray-500">Rate Limit:</span>
          <p className="text-gray-900 mt-1">{apiKey?.rateLimitPerMinute}/min</p>
        </div>
        {apiKey?.lastUsedAt && (
          <div>
            <span className="text-gray-500">Last Used:</span>
            <p className="text-gray-900 mt-1">
              {new Date(apiKey.lastUsedAt)?.toLocaleDateString()}
            </p>
          </div>
        )}
        {apiKey?.expiresAt && (
          <div>
            <span className="text-gray-500">Expires:</span>
            <p className="text-gray-900 mt-1">
              {new Date(apiKey.expiresAt)?.toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
      {/* Actions */}
      {apiKey?.status === 'active' && (
        <div className="flex gap-2 pt-4 border-t">
          <Button
            onClick={() => onViewUsage(apiKey)}
            variant="secondary"
            className="flex-1 flex items-center justify-center gap-2"
          >
            <TrendingUp className="w-4 h-4" />
            View Usage
          </Button>
          <Button
            onClick={() => onRegenerate(apiKey?.id)}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Regenerate
          </Button>
          <Button
            onClick={() => onRevoke(apiKey?.id)}
            variant="danger"
            className="flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Revoke
          </Button>
        </div>
      )}
    </div>
  );
}